import { useState, useEffect } from "react";
import api from "../lib/api";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { AlertTriangle, Bell, FileText, Activity, Clock } from "lucide-react";

const typeIcons = { audit: FileText, alert: Bell, incident: AlertTriangle };
const typeColors = { audit: "#29B5E8", alert: "#F59E0B", incident: "#EF4444" };
const sevStyles = { critical: { bg: "rgba(190,52,85,0.08)", color: "#BE3455" }, high: { bg: "rgba(231,111,81,0.08)", color: "#E76F51" }, medium: { bg: "rgba(234,179,8,0.08)", color: "#C8963E" }, low: { bg: "rgba(30,58,95,0.08)", color: "#1E3A5F" }, info: { bg: "rgba(255,255,255,0.04)", color: "#5A6B82" } };

export default function EventsExplorerPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState("all");

  useEffect(() => {
    const params = typeFilter !== "all" ? { event_type: typeFilter } : {};
    api.get("/events", { params }).then((r) => { setEvents(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [typeFilter]);

  if (loading) return <div className="p-6" data-testid="events-loading"><Skeleton className="h-8 w-48 bg-white/5" /></div>;

  return (
    <div className="p-6 space-y-4" data-testid="events-explorer-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[26px] font-extrabold tracking-tight text-[#2D3142]">Events Explorer</h1>
          <p className="text-[13px] mt-0.5" style={{ color: "#6B7280" }}>{events.length} events in timeline</p>
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[150px] h-9 rounded-lg bg-black/[0.02] border-gray-200 text-[#8899AA]" data-testid="event-type-filter"><SelectValue placeholder="All Events" /></SelectTrigger>
          <SelectContent style={{ background: "#FFFFFF", border: "1px solid #E8E4DF" }}>
            <SelectItem value="all">All Events</SelectItem>
            <SelectItem value="incident">Incidents</SelectItem>
            <SelectItem value="alert">Alerts</SelectItem>
            <SelectItem value="audit">Audit</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="sf-card overflow-hidden" data-testid="events-timeline">
        {events.map((ev, i) => {
          const Icon = typeIcons[ev.type] || Activity;
          const color = typeColors[ev.type] || "#5A6B82";
          const sev = sevStyles[ev.severity] || sevStyles.info;
          return (
            <div key={`${ev.event_id}-${i}`} className="flex gap-4 px-5 py-3.5 hover:bg-gray-50 transition-colors animate-fade-up" style={{ borderBottom: "1px solid rgba(255,255,255,0.03)", animationDelay: `${Math.min(i, 20) * 25}ms` }} data-testid={`event-${ev.event_id}`}>
              <div className="flex flex-col items-center pt-0.5">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${color}12` }}>
                  <Icon className="w-4 h-4" style={{ color }} />
                </div>
                {i < events.length - 1 && <div className="w-px flex-1 mt-1" style={{ background: "rgba(45,49,66,0.02)" }} />}
              </div>
              <div className="flex-1 min-w-0 pb-1">
                <div className="flex items-center gap-2 mb-1">
                  <Badge className="text-[8px] uppercase font-bold border-0 rounded-md" style={{ background: `${color}15`, color }}>{ev.type}</Badge>
                  {ev.subtype && <Badge className="text-[8px] uppercase font-bold border-0 rounded-md" style={{ background: sev.bg, color: sev.color }}>{ev.subtype?.replace(/_/g, " ")}</Badge>}
                  <span className="text-[10px] font-mono ml-auto flex items-center gap-1" style={{ color: "#9CA3AF" }}>
                    <Clock className="w-3 h-3" />{ev.timestamp?.slice(0, 16).replace("T", " ")}
                  </span>
                </div>
                <p className="text-[13px] font-medium text-[#C0CDD8] truncate">{ev.title}</p>
                {ev.detail && ev.detail !== ev.title && <p className="text-[11px] mt-0.5 truncate" style={{ color: "#6B7280" }}>{ev.detail}</p>}
                <span className="text-[10px] font-mono" style={{ color: "#9CA3AF" }}>{ev.actor}</span>
              </div>
            </div>
          );
        })}
        {events.length === 0 && <div className="px-5 py-12 text-center text-sm font-mono" style={{ color: "#9CA3AF" }}>[ No events ]</div>}
      </div>
    </div>
  );
}
